import {
  addCommasToNumber,
  ErrorNotification,
  GraviButton,
  Horizontal,
  NotificationMessage,
  Texto,
  Vertical,
} from '@gravitate-js/excalibrr'
import { useEffect, useState } from 'react'
import { BookOutlined, FileTextFilled, PlusOutlined } from '@ant-design/icons'
import { Form, Input, Popover, Select, Tooltip } from 'antd'
import { useValuation } from '@api/useValuation'
import { NoteButton } from '@components/InventoryValuation/Costing/Details/CreateManualLayerForm'
import classNames from 'classnames'
import moment from 'moment'
import { useFormatter } from '@api/useFormatter'
import { colorTypeMatch, removeUnderscore } from './util'

export const DeliveryRowDetails = ({
  row,
  reasonCodes,
  inventory_costing_identifier,
  setTotals,
  totals,
  canEdit,
  date,
}) => {
  const [isAddingAdjustment, setIsAddingAdjustment] = useState(false)
  const { addAdjustmentMutation } = useValuation()
  const { mutateAsync: addAdjustment } = addAdjustmentMutation()
  const [hasEither, setHasEither] = useState({ volume: false, value: false })
  const { formatCurrency } = useFormatter()
  const formattedCurrency = formatCurrency(null, true)
  useEffect(() => {
    if (row?.volume && row?.value) {
      setTotals({
        volume: addCommasToNumber(row.volume, 0),
        value: formattedCurrency + addCommasToNumber(row.value, 2),
      })
    }
  }, [row?.volume, row?.value])

  const [form] = Form.useForm()
  const adjustmentValidator = (rule, value, callback) => {
    if (!hasEither.value && !hasEither.volume) return callback('Volume or value required')
    callback()
  }
  const handleChange = (e) => {
    const name = e.target.id.split('_')[1]
    if (e.target.value && e.target.value !== '0') {
      setHasEither({ ...hasEither, [name]: true })
      form.validateFields(['volume', 'value'])
    } else {
      setHasEither({ ...hasEither, [name]: false })
    }
  }
  const onFinish = async (values) => {
    const request = {
      ...values,
      type: 'adjustment',
      inventory_costing_identifier,
      order_number: row?.order_number,
      date: moment(date).set({ hour: 0, minute: 0, second: 0, millisecond: 0 }).format('YYYY-MM-DD HH:mm:ss'),
    }
    try {
      await addAdjustment(request).then(async () => {
        NotificationMessage('New adjustment added', 'New adjustment added successfully', false)
        form.resetFields()
        setHasEither(false)
        setIsAddingAdjustment(false)
      })
    } catch (e) {
      NotificationMessage('Problem saving new adjustment', `ERROR: ${e}`, true)
    }

    setIsAddingAdjustment(false)
    form.resetFields()
  }

  return (
    <>
      {row.bols.map((bol, index) => (
        <Horizontal key={'bol_delivery_row' + index} className='bg-1 border-bottom'>
          <Vertical
            justifyContent={'center'}
            className='p-2'
            flex={1}
            style={{ background: 'var(--theme-color-2-dim)' }}
          >
            <Texto className='text-secondary' category='label'>
              <BookOutlined fill={'var(--theme-color-2)'} className='mr-2' /> BOL
            </Texto>
          </Vertical>
          <Vertical
            className='p-2'
            justifyContent={'center'}
            flex={1.5}
            style={{ background: 'var(--theme-color-2-dim)' }}
          >
            <Texto category='label' align='right' className='text-secondary text-truncate' style={{ width: '100%' }}>
              {bol.product}
            </Texto>
          </Vertical>
          <Vertical className='p-2' flex={1} justifyContent={'center'} alignItems={'center'}>
            <Texto className='text-secondary text-truncate' align={'center'} style={{ width: '100%' }}>
              {bol.number}
            </Texto>
          </Vertical>
          <Vertical className='p-2' flex={1} />
          <Vertical className='p-2' flex={1.5} justifyContent={'center'}>
            <Texto className='text-truncate' align={'center'} style={{ width: '100%' }}>
              {bol.supplier}
            </Texto>
          </Vertical>
          <Vertical className='p-2' flex={1} justifyContent={'center'} alignItems={'flex-end'}>
            {addCommasToNumber(bol.volume ?? 0, 0)}
          </Vertical>
          <Vertical className='p-2' flex={1} justifyContent={'center'} alignItems={'flex-end'}>
            {bol.price?.price && formattedCurrency}
            {addCommasToNumber(bol.price?.price, 4)}
          </Vertical>
          <Vertical className='p-2 mr-2' flex={1} justifyContent={'center'} alignItems={'flex-end'}>
            {bol.value && formattedCurrency}
            {addCommasToNumber(bol.value, 2)}
          </Vertical>
        </Horizontal>
      ))}
      {row.modifiers.map((modifier, index) => {
        const modifierType = modifier.type.toUpperCase()
        return (
          <Horizontal key={'modifier_delivery_row' + index} className='bg-1 border-bottom' style={{ width: '100%' }}>
            <Vertical className='p-2 pr-4' flex={2.5} style={{ background: colorTypeMatch?.[modifierType]?.bg }}>
              <Texto category='label' style={{ color: colorTypeMatch?.[modifierType]?.color }}>
                {colorTypeMatch?.[modifierType]?.icon} {modifierType} {modifier.reason_code}
              </Texto>
            </Vertical>

            <Vertical className='p-2' flex={1} />
            <Vertical className='p-2' flex={1} />
            <Vertical className='p-2' flex={1.5} />
            <Vertical className='p-2' flex={1} justifyContent={'center'} alignItems={'flex-end'}>
              <Texto className='text-truncate' appearance={modifier?.volume && modifier.volume < 0 ? 'warning' : ''}>
                {addCommasToNumber(modifier.volume ?? 0, 0)}
              </Texto>
            </Vertical>
            <Vertical className='p-2' flex={1} justifyContent={'center'} alignItems={'flex-end'}>
              {modifier.price && formattedCurrency}
              {addCommasToNumber(modifier.price, 4)}
            </Vertical>
            <Vertical className='p-2 mr-2' flex={1} justifyContent={'center'} alignItems={'flex-end'}>
              <Texto className='text-truncate' appearance={modifier?.value && modifier.value < 0 ? 'warning' : ''}>
                {formattedCurrency}
                {addCommasToNumber(modifier.value || 0, 2)}
              </Texto>
            </Vertical>
          </Horizontal>
        )
      })}
      {row.adjustments.map((adjustment, index) => {
        const type = adjustment.type.toUpperCase()
        const content = !adjustment?.notes ? null : (
          <>
            <Texto style={{ color: 'var(--gray-400)', fontWeight: 'bold' }}>NOTE</Texto>
            <Texto> {adjustment.notes} </Texto>
          </>
        )
        const title = <Texto category={'h6'}>{adjustment?.reason_code}</Texto>
        return (
          <Horizontal key={'adjustment_delivery_row' + index} className='bg-1 border-bottom' style={{ width: '100%' }}>
            <Horizontal flex={3} style={{ background: colorTypeMatch?.[type]?.bg }}>
              <Vertical
                justifyContent={'center'}
                className='p-2'
                flex={1}
                style={{ background: colorTypeMatch?.[type]?.bg }}
              >
                <Texto className={'text-truncate '} style={{ color: colorTypeMatch?.[type]?.color }}>
                  {colorTypeMatch?.[type]?.icon} {removeUnderscore(type)}
                </Texto>
              </Vertical>

              {type === 'ADJUSTMENT' && (
                <Vertical
                  className='p-2'
                  justifyContent={'center'}
                  flex='0'
                  style={{ overflow: 'visible', background: colorTypeMatch?.[type]?.bg }}
                >
                  {adjustment.notes ? (
                    <Popover content={content} title={title}>
                      <GraviButton icon={<FileTextFilled />} size='small' />
                    </Popover>
                  ) : (
                    <Tooltip color={'var(--bg-1)'} title={<Texto>{adjustment?.reason_code} </Texto>}>
                      <GraviButton
                        buttonText={
                          <Texto align='right' category={'span'} className='text-truncate text-gray-600'>
                            {adjustment.reason_code}
                          </Texto>
                        }
                      />
                    </Tooltip>
                  )}
                </Vertical>
              )}
            </Horizontal>

            <Vertical className='p-2' flex={1} />
            <Vertical className='p-2' flex={1} />
            <Vertical className='p-2' flex={1.5} />
            <Vertical className='p-2' flex={1} justifyContent={'center'} alignItems={'flex-end'}>
              <Texto weight={'bold'} appearance={'warning'}>
                {addCommasToNumber(adjustment.volume ?? 0, 0)}
              </Texto>
            </Vertical>
            <Vertical className='p-2' weight='bold' flex={1} justifyContent={'center'} alignItems={'flex-end'}>
              {adjustment.price && formattedCurrency}
              {addCommasToNumber(adjustment.price, 4)}
            </Vertical>
            <Vertical className='p-2 mr-2' flex={1} justifyContent={'center'} alignItems={'flex-end'}>
              <Texto weight={'bold'} appearance={'warning'}>
                {formattedCurrency}
                {addCommasToNumber(adjustment.value || 0, 2)}
              </Texto>
            </Vertical>
          </Horizontal>
        )
      })}
      <Form
        form={form}
        name={'addAdjustmentForm'}
        onFinish={onFinish}
        onFinishFailed={ErrorNotification}
        autoComplete='off'
      >
        <Horizontal className={classNames('bg-1 border-bottom', { 'add-adjustment-row': isAddingAdjustment })}>
          <Vertical
            justifyContent={'center'}
            className='pl-2 pr-4'
            bg='primary'
            flex={2.5}
            style={{ minHeight: '37px', maxHeight: 'fit-content' }}
          >
            <Horizontal style={{ width: '100%' }} alignItems={'center'}>
              <Texto style={{ flex: 1 }} className=' text-ellipsis'>
                TOTAL
              </Texto>
              {canEdit && !isAddingAdjustment && (
                <GraviButton
                  size='small'
                  icon={<PlusOutlined />}
                  buttonText={'Adjustment'}
                  onClick={() => setIsAddingAdjustment(true)}
                  appearance={'outline'}
                />
              )}
              {canEdit && isAddingAdjustment && (
                <>
                  <GraviButton
                    buttonText={'Cancel'}
                    size='small'
                    className='mr-2'
                    onClick={() => {
                      setIsAddingAdjustment(false)
                      form.resetFields()
                    }}
                  />
                  <GraviButton theme2 size='small' buttonText='Save' htmlType='submit' />
                </>
              )}
            </Horizontal>
          </Vertical>
          {!isAddingAdjustment && (
            <>
              <Vertical className='p-2' flex={1} />
              <Vertical className='p-2' flex={1} />
              <Vertical className='p-2' flex={1.5} />
              <Vertical className='p-2' flex={1} alignItems={'flex-end'}>
                {totals.volume}
              </Vertical>
              <Vertical className='p-2' flex={1} alignItems={'flex-end'}>
                {row.price && formattedCurrency}
                {addCommasToNumber(row.price, 4)}
              </Vertical>
              <Vertical
                className='p-2 mr-2 full-height-width'
                flex={1}
                justifyContent={'center'}
                alignItems={'flex-end'}
              >
                {totals.value}
              </Vertical>
            </>
          )}
          {canEdit && isAddingAdjustment && (
            <>
              <Vertical justifyContent={'center'} className='pl-2 pr-2' flex={1.5}>
                <NoteButton form={form} />
              </Vertical>
              <Vertical flex={2} className={'pl-2 pr-2 add-adjustment-item'}>
                <Form.Item
                  style={{ paddingTop: '0.1em' }}
                  name='reason_code'
                  rules={[{ required: true, message: 'Reason is required.' }]}
                >
                  <Select placeholder={'Select Reason'} className={'add-adjustment-select p-0'}>
                    {reasonCodes?.adjustment_reason_codes?.map((reason) => (
                      <Select.Option value={reason} key={reason}>
                        {reason}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </Vertical>
              <Vertical className='p-2' flex={0.25} />
              <Vertical className='pl-2 pr-2 add-adjustment-item' flex={1.25} alignItems={'flex-start'}>
                <Form.Item name='volume' rules={[{ validator: adjustmentValidator }]}>
                  <Input placeholder={'Volume'} type={'number'} onChange={handleChange} />
                </Form.Item>
              </Vertical>

              <Vertical className='p-2' flex={0.25} />

              <Vertical className='mr-2 pr-2 pl-2 add-adjustment-item' flex={1.25} alignItems={'flex-start'}>
                <Form.Item name='value' rules={[{ validator: adjustmentValidator }]}>
                  <Input placeholder={'Value'} type={'number'} onChange={handleChange} />
                </Form.Item>
              </Vertical>
            </>
          )}
        </Horizontal>
      </Form>
    </>
  )
}
